/**
 * Child Profile Form Options
 * Constants for various autocomplete options in the Add Child Modal
 */

export const CARE_FOCUS_OPTIONS = [
  { code: "ASD", label: "Autism" },
  { code: "DEMENTIA", label: "Dementia" },
  { code: "CHRONIC", label: "Chronic illness" },
  { code: "PALLIATIVE_CARE", label: "Palliative care" },
  { code: "HOSPICE", label: "Hospice care" },
  { code: "GENERAL_CARE", label: "General care" },
];

export const ADDITIONAL_SUPPORT_OPTIONS = [
  { code: "ADHD", label: "ADHD" },
  { code: "SPEECH", label: "Communication support" },
  { code: "LEARN", label: "Learning support" },
  { code: "SPECIAL_EDUCATION", label: "Special education" },
  { code: "DEVELOPMENTAL_DELAYS", label: "Developmental delays" },
  { code: "SEIZURE_TRACKING", label: "Seizure tracking" },
  { code: "MEDICAL", label: "Health condition" },
  { code: "BEHAVIOR", label: "Mood / emotional support" },
  { code: "MEMORY", label: "Memory support" },
  { code: "ELDER_CARE", label: "Elderly care" },
  { code: "AGING", label: "Aging support" },
  { code: "DISABILITY", label: "Disability support" },
  { code: "MEDICATION_COORDINATION", label: "Medication coordination" },
  { code: "MOBILITY", label: "Mobility support" },
  { code: "POST_SURGERY", label: "Post-surgery recovery" },
  { code: "REHABILITATION", label: "Rehabilitation" },
  { code: "RECOVERY", label: "Surgery / injury recovery" },
  { code: "MENTAL_HEALTH", label: "Mental health support" },
  { code: "CHILDCARE_ACROSS_HOUSEHOLDS", label: "Childcare across households" },
  { code: "FOSTER_CARE", label: "Foster care" },
  { code: "PET_CARE", label: "Pet care" },
  { code: "DAILY_CARE", label: "Daily care support" },
];

export const CONDITION_OPTIONS = [
  ...CARE_FOCUS_OPTIONS,
  ...ADDITIONAL_SUPPORT_OPTIONS,
];

const PRIMARY_CARE_FOCUS_CODES = new Set([
  ...CARE_FOCUS_OPTIONS.map((item) => item.code),
]);

const PRIORITY_PRIMARY_CARE_FOCUS_CODES = [
  "HOSPICE",
  "PALLIATIVE_CARE",
  "DEMENTIA",
  "CHRONIC",
  "ASD",
  "GENERAL_CARE",
];

const getOptionKey = (item = {}) => String(item?.code || item?.label || item || "").toUpperCase();

export const isPrimaryCareFocusOption = (item = {}) => (
  PRIMARY_CARE_FOCUS_CODES.has(String(item?.code || "").toUpperCase())
);

export const normalizeCareFocusSelection = (items = []) => {
  const normalizedItems = Array.isArray(items) ? items.filter(Boolean) : [items].filter(Boolean);
  const priorityItem = PRIORITY_PRIMARY_CARE_FOCUS_CODES
    .map((code) => normalizedItems.find((item) => getOptionKey(item) === code))
    .find(Boolean);

  if (priorityItem) {
    return [
      priorityItem,
      ...normalizedItems.filter((item) => item !== priorityItem),
    ];
  }

  const primaryItem = normalizedItems.find(isPrimaryCareFocusOption);
  if (!primaryItem) return normalizedItems;

  return [
    primaryItem,
    ...normalizedItems.filter((item) => item !== primaryItem),
  ];
};

export const splitCareFocusSelection = (items = []) => {
  const normalized = normalizeCareFocusSelection(items);
  const primaryFocus = normalized.find(isPrimaryCareFocusOption) || null;

  return {
    primaryFocus,
    additionalSupports: normalized.filter((item) => item !== primaryFocus),
  };
};

export const composeCareFocusSelection = (primaryFocus, additionalSupports = []) => (
  normalizeCareFocusSelection([
    primaryFocus,
    ...(Array.isArray(additionalSupports) ? additionalSupports : [additionalSupports]),
  ].filter(Boolean))
);

export const FOOD_ALLERGY_OPTIONS = [
  "Dairy/Milk",
  "Gluten/Wheat",
  "Nuts",
  "Eggs",
  "Soy",
  "Shellfish",
  "Fish",
  "Sesame",
  "Food dyes",
  "Artificial sweeteners",
  "Citrus",
  "Chocolate",
];

export const DIETARY_OPTIONS = [
  "Gluten-free",
  "Dairy-free",
  "Sugar-limited",
  "Low-FODMAP",
  "Casein-free",
  "Dye-free",
  "Organic only",
  "Limited processed foods",
];

export const SENSORY_OPTIONS = [
  "Noise sensitivity",
  "Light sensitivity",
  "Touch sensitivity",
  "Texture preferences",
  "Smell sensitivity",
  "Taste sensitivity",
  "Temperature sensitivity",
  "Movement needs",
  "Pain sensitivity",
  "Needs quiet space",
  "Prefers familiar spaces",
];

export const TRIGGER_OPTIONS = [
  "Loud environments",
  "Crowds",
  "Bright lights",
  "Transitions/changes",
  "Hunger",
  "Fatigue",
  "Pain or discomfort",
  "Being rushed",
  "Too many instructions",
  "Missed routine",
  "Certain foods",
  "Screen time limits",
  "Social situations",
  "New environments",
  "Memory confusion",
];

export const SLEEP_OPTIONS = [
  "Difficulty falling asleep",
  "Frequent night waking",
  "Early morning waking",
  "Restless sleep",
  "Needs specific routine",
  "Sensory needs for sleep",
  "Nightmares/night terrors",
  "Sleep walking",
];

export const COMMUNICATION_OPTIONS = [
  "Needs extra processing time",
  "Prefers simple instructions",
  "Prefers written reminders",
  "Prefers choices",
  "May go quiet when overwhelmed",
  "Uses AAC device",
  "Uses gestures",
  "Sign language",
  "Picture cards",
  "Needs help advocating",
  "Hearing support",
  "Vision support",
  "Language interpretation",
];
